import React, { useState, useEffect } from 'react';
import { XMarkIcon, ShoppingCartIcon, TruckIcon, CheckCircleIcon, CreditCardIcon, ExclamationTriangleIcon } from '../icons/HeroIcons';
import { StripePaymentForm } from './StripePaymentForm';
import { SavedCardsPayment } from './SavedCardsPayment';
import { AISHA_API_BASE } from '../../constants';
import { getErrorInfo, isAdminContactRequired, ErrorCode } from '../../constants/errorCodes';

interface PrintfulPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  imageUrl: string;
  catalogId: string;
  goodsName?: string;
  mockupUrl?: string;
  merchandiseId?: number;  // SuzuriMerchandise IDを受け取る
  onPurchaseCompleted?: () => void;  // 購入完了時のコールバック
}

interface Variant {
  variant_id: number;
  product_id: number;
  name: string;
  size: string;
  color: string;
  color_code: string;
  image?: string;  // バリアント画像URL
  price: number;
  in_stock: boolean;
  is_available: boolean;
  is_japan_available?: boolean;
}

interface ShippingAddress {
  name: string;
  email: string;
  phone: string;
  zip: string;
  state_name: string;
  city: string;
  address1: string;
  address2: string;
}

// 都道府県リスト
const JAPAN_PREFECTURES = [
  '北海道',
  '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県',
  '沖縄県'
];

const PrintfulPurchaseModal: React.FC<PrintfulPurchaseModalProps> = ({
  isOpen,
  onClose,
  userId,
  imageUrl,
  catalogId,
  goodsName = 'カスタム商品',
  mockupUrl,
  merchandiseId: propsMerchandiseId,  // propsから受け取る
  onPurchaseCompleted  // 購入完了時のコールバック
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: バリアント選択, 2: 配送先入力, 3: 確認, 4: 決済, 5: 完了
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    name: '',
    email: '',
    phone: '',
    zip: '',
    state_name: '',
    city: '',
    address1: '',
    address2: ''
  });
  const [saveAddress, setSaveAddress] = useState<boolean>(true); // デフォルトで保存するにチェック
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [testMode, setTestMode] = useState(false); // デフォルトは通常注文（安全側に倒す）
  const [isAdmin, setIsAdmin] = useState(false); // 管理者フラグ
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null); // 商品画像URL
  const [merchandiseId, setMerchandiseId] = useState<number | null>(propsMerchandiseId || null);  // propsから初期値を設定
  const [creatingMerchandise, setCreatingMerchandise] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [useNewCard, setUseNewCard] = useState(false);

  const fetchSavedCards = async () => {
    try {
      const response = await fetch(`${AISHA_API_BASE}/stripe/saved-cards/?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setSavedCards(data.payment_methods || []);
      }
    } catch (err) {
      console.error('保存済みカード取得エラー:', err);
    }
  };

  useEffect(() => {
    if (isOpen && catalogId) {
      fetchVariants();
      fetchSavedAddress(); // 保存済み住所を取得
      fetchSavedCards(); // 保存済みカードを取得
      checkAdminStatus(); // 管理者ステータスを確認
    }
    // propsからmerchandiseIdが渡された場合は更新
    if (propsMerchandiseId && propsMerchandiseId !== merchandiseId) {
      setMerchandiseId(propsMerchandiseId);
    }
  }, [isOpen, catalogId, propsMerchandiseId]);

  // 管理者でない場合はtestModeを強制的にfalseに
  useEffect(() => {
    if (!isAdmin) {
      setTestMode(false);
    }
  }, [isAdmin]);

  // 数量が変更されたときに送料を再計算
  useEffect(() => {
    if (currentStep === 2 && selectedVariant && shippingAddress.zip) {
      calculateShipping();
    }
  }, [quantity]);

  const fetchSavedAddress = async () => {
    setLoadingAddress(true);
    try {
      const response = await fetch(`${AISHA_API_BASE}/shipping-address/?user_id=${userId}`);
      const data = await response.json();
      
      if (data.success && data.has_address) {
        setShippingAddress(data.address);
      }
    } catch (err) {
      console.error('住所取得エラー:', err);
    } finally {
      setLoadingAddress(false);
    }
  };

  const saveShippingAddress = async () => {
    if (!saveAddress) return;
    
    try {
      await fetch(`${AISHA_API_BASE}/shipping-address/save/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          address: shippingAddress
        })
      });
    } catch (err) {
      console.error('住所保存エラー:', err);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await fetch(`${AISHA_API_BASE}/users/${userId}/admin-status/`);
      const data = await response.json();
      
      if (data.is_admin) {
        setIsAdmin(true);
        // 管理者の場合もデフォルトはfalse（安全側）、チェックボックスで変更可能
      } else {
        setIsAdmin(false);
        setTestMode(false); // 管理者でない場合はテストモードを強制的に無効化
      }
    } catch (err) {
      console.error('管理者ステータス確認エラー:', err);
      // エラーの場合は安全のために一般ユーザーとして扱う
      setIsAdmin(false);
      setTestMode(false);
    }
  };


  const fetchVariants = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${AISHA_API_BASE}/printful/catalog/${catalogId}/variants/`);
      const data = await response.json();
      
      if (data.success) {
        // 商品画像URLを設定
        if (data.product_info && data.product_info.product_image) {
          setProductImageUrl(data.product_info.product_image);
        }
        
        // バリアントに価格情報と在庫状態を追加
        const variantsWithPrice = data.variants.map((variant: any) => ({
          ...variant,
          price: variant.price || 3000, // デフォルト価格
          is_available: variant.in_stock && (variant.is_japan_available !== false) // 在庫があり、日本配送可能
        }));
        setVariants(variantsWithPrice);
      } else {
        setError('バリアント情報の取得に失敗しました');
      }
    } catch (err) {
      setError('エラーが発生しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateShipping = async () => {
    if (!selectedVariant || !shippingAddress.zip) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${AISHA_API_BASE}/printful/purchase/calculate-shipping/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient: {
            ...shippingAddress,
            country_code: 'JP'
          },
          variant_id: selectedVariant.variant_id,
          quantity: quantity,
          catalog_id: catalogId  // 送料上乗せ額を取得するために必要
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setShippingCost(data.shipping_cost);
      }
    } catch (err) {
      console.error('送料計算エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVariantSelect = (variant: Variant) => {
    setSelectedVariant(variant);
  };

  const createPrintfulMerchandise = async () => {
    if (!selectedVariant) return false;
    
    setCreatingMerchandise(true);
    setError('');
    
    try {
      // Printful商品をSuzuriMerchandiseテーブルに保存
      const response = await fetch(`${AISHA_API_BASE}/printful/create-merchandise/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          image_url: imageUrl,
          catalog_id: catalogId,
          variant_id: selectedVariant.variant_id,
          product_id: selectedVariant.product_id,
          product_name: goodsName,
          variant_name: selectedVariant.name,
          size: selectedVariant.size,
          color: selectedVariant.color,
          price: selectedVariant.price
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMerchandiseId(data.merchandise_id);
        return true;
      } else {
        setError(data.error || 'グッズ作成に失敗しました');
        return false;
      }
    } catch (err) {
      console.error('Error creating merchandise:', err);
      setError('グッズ作成中にエラーが発生しました');
      return false;
    } finally {
      setCreatingMerchandise(false);
    }
  };

  const handleNextStep = async () => {
    if (currentStep === 1 && selectedVariant) {
      // ステップ1から直接ステップ2（配送先入力）へ
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // 配送先入力完了後、送料を計算
      await calculateShipping();
      // 住所を保存
      await saveShippingAddress();
      setCurrentStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateShippingForm = () => {
    return shippingAddress.name && 
           shippingAddress.email && 
           shippingAddress.phone && 
           shippingAddress.zip && 
           shippingAddress.state_name && 
           shippingAddress.city && 
           shippingAddress.address1;
  };

  const handleTestOrder = async () => {
    setLoading(true);
    setError('');
    
    const orderData = {
      user_id: userId,
      image_url: imageUrl,
      catalog_id: catalogId,
      variant_id: selectedVariant?.variant_id,
      quantity: quantity,
      recipient: {
        ...shippingAddress,
        country_code: 'JP'
      },
      shipping_cost: shippingCost,
      suzuri_merchandise_id: merchandiseId,  // SuzuriMerchandise IDを追加
      test_mode: testMode
    };
    
    try {
      const response = await fetch(`${AISHA_API_BASE}/admin/printful/purchase/test-order/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOrderResult(data);
        setCurrentStep(5); // 完了画面へ
        // 購入完了時のコールバックを呼び出す
        if (onPurchaseCompleted) {
          onPurchaseCompleted();
        }
      } else {
        console.error('Order error:', data);
        setError(data.error || '注文の作成に失敗しました');
        if (data.received_data) {
          console.error('Received data:', data.received_data);
        }
      }
    } catch (err) {
      setError('エラーが発生しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const subtotal = selectedVariant ? selectedVariant.price * quantity : 0;
  const tax = (subtotal + shippingCost) * 0.1;
  const total = subtotal + shippingCost + tax;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[65] p-4">
      <div 
        className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center">
            <ShoppingCartIcon className="w-6 h-6 mr-2" />
            商品を購入
            {testMode && <span className="ml-2 px-2 py-1 bg-yellow-600 text-yellow-100 text-xs rounded">テストモード</span>}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* プログレスバー */}
        <div className="px-4 py-3 bg-gray-750">
          <div className="flex items-center justify-between">
            <div className={`flex-1 text-center ${currentStep >= 1 ? 'text-indigo-400' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                1
              </div>
              <span className="text-xs mt-1">サイズ・色</span>
            </div>
            <div className={`flex-1 text-center ${currentStep >= 2 ? 'text-indigo-400' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                2
              </div>
              <span className="text-xs mt-1">配送先</span>
            </div>
            <div className={`flex-1 text-center ${currentStep >= 3 ? 'text-indigo-400' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                3
              </div>
              <span className="text-xs mt-1">確認</span>
            </div>
            <div className={`flex-1 text-center ${currentStep >= 4 ? 'text-indigo-400' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= 4 ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                4
              </div>
              <span className="text-xs mt-1">決済</span>
            </div>
            <div className={`flex-1 text-center ${currentStep >= 5 ? 'text-indigo-400' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= 5 ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                5
              </div>
              <span className="text-xs mt-1">完了</span>
            </div>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="p-4">
          {/* エラー画面 */}
          {showErrorScreen && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-900/50 rounded-full mb-4">
                <ExclamationTriangleIcon className="w-10 h-10 text-red-400" />
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-2">処理に失敗しました</h3>
              
              <div className="max-w-md mx-auto mb-6">
                <p className="text-gray-300 mb-2">{errorMessage}</p>
                {errorCode && (
                  <p className="text-sm text-gray-500">エラーコード: {errorCode}</p>
                )}
              </div>
              
              {errorCode && isAdminContactRequired(errorCode) && (
                <div className="p-4 bg-yellow-900/30 border border-yellow-600 rounded-lg max-w-md mx-auto mb-6">
                  <p className="text-yellow-400 text-sm">
                    このエラーは管理者による対応が必要です。
                    上記のエラーコードをお控えの上、サポートまでお問い合わせください。
                  </p>
                </div>
              )}
              
              <button
                onClick={() => {
                  setShowErrorScreen(false);
                  setErrorCode(null);
                  setErrorMessage('');
                  setCurrentStep(3); // 確認画面に戻る
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
              >
                戻る
              </button>
            </div>
          )}
          
          {/* 商品情報 */}
          {!showErrorScreen && currentStep < 5 && (
            <div className="mb-4 p-3 bg-gray-700 rounded-lg flex items-center space-x-3">
              {/* 商品画像 */}
              {productImageUrl && (
                <div className="relative">
                  <img src={productImageUrl} alt="商品" className="w-16 h-16 object-cover rounded" />
                  <span className="absolute -bottom-1 -right-1 text-xs bg-green-800 px-1 rounded">商品</span>
                </div>
              )}
              {/* デザイン画像 */}
              {imageUrl && (
                <div className="relative">
                  <img src={imageUrl} alt="オリジナル" className="w-16 h-16 object-cover rounded" />
                  <span className="absolute -bottom-1 -right-1 text-xs bg-gray-800 px-1 rounded">デザイン</span>
                </div>
              )}
              {/* プレビュー画像 */}
              {mockupUrl && (
                <div className="relative">
                  <img src={mockupUrl} alt="モックアップ" className="w-16 h-16 object-cover rounded" />
                  <span className="absolute -bottom-1 -right-1 text-xs bg-indigo-800 px-1 rounded">プレビュー</span>
                </div>
              )}
              <div>
                <h3 className="text-white font-semibold">{goodsName}</h3>
                {selectedVariant && (
                  <p className="text-sm text-gray-400">
                    {selectedVariant.size} {selectedVariant.color && `- ${selectedVariant.color}`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ステップ1: バリアント選択 */}
          {!showErrorScreen && currentStep === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">サイズと色を選択</h3>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                </div>
              ) : error ? (
                <div className="text-red-400 text-center py-4">{error}</div>
              ) : (
                <>
                  {/* バリアント選択 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    {variants.map((variant) => (
                      <button
                        key={variant.variant_id}
                        onClick={() => handleVariantSelect(variant)}
                        disabled={!variant.is_available}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          selectedVariant?.variant_id === variant.variant_id
                            ? 'border-indigo-500 bg-indigo-900/30'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        } ${!variant.is_available ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center space-x-3">
                          {/* バリアント画像 */}
                          {variant.image && (
                            <img 
                              src={variant.image} 
                              alt={variant.name || `${variant.size} variant`}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 flex justify-between items-start">
                            <div>
                              <div className="font-medium text-white">
                                {variant.size}
                                {variant.color && (
                                  <span className="ml-2">
                                    {variant.color_code && (
                                      <span
                                        className="inline-block w-4 h-4 rounded-full border border-gray-600 align-middle mr-1"
                                        style={{ backgroundColor: variant.color_code }}
                                      />
                                    )}
                                    {variant.color}
                                  </span>
                                )}
                              </div>
                              <div className="text-lg font-bold text-indigo-300 mt-1">
                                ¥{Math.floor(variant.price).toLocaleString()}
                              </div>
                            </div>
                            {!variant.is_available && (
                              <span className="text-xs text-red-400">在庫なし</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* 数量選択 - バリアント選択後に表示 */}
                  {selectedVariant && (
                    <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                      <label className="block text-sm text-gray-400 mb-2">数量</label>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-8 h-8 rounded bg-gray-600 hover:bg-gray-500 text-white font-bold transition-colors"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 px-3 py-1 bg-gray-800 text-white text-center rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                          min="1"
                          max="10"
                        />
                        <button
                          onClick={() => setQuantity(Math.min(10, quantity + 1))}
                          className="w-8 h-8 rounded bg-gray-600 hover:bg-gray-500 text-white font-bold transition-colors"
                        >
                          +
                        </button>
                        <span className="text-sm text-gray-400">個</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-600">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">小計：</span>
                          <span className="text-lg font-bold text-white">
                            ¥{Math.floor(selectedVariant.price * quantity).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ステップ2: 配送先入力 */}
          {!showErrorScreen && currentStep === 2 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <TruckIcon className="w-5 h-5 mr-2" />
                配送先情報
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">お名前 *</label>
                    <input
                      type="text"
                      value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">メールアドレス *</label>
                    <input
                      type="email"
                      value={shippingAddress.email}
                      onChange={(e) => setShippingAddress({...shippingAddress, email: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">電話番号 *</label>
                    <input
                      type="tel"
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">郵便番号 *</label>
                    <input
                      type="text"
                      value={shippingAddress.zip}
                      onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">都道府県 *</label>
                    <select
                      value={shippingAddress.state_name}
                      onChange={(e) => setShippingAddress({...shippingAddress, state_name: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none appearance-none cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      {JAPAN_PREFECTURES.map((prefecture) => (
                        <option key={prefecture} value={prefecture}>
                          {prefecture}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">市区町村 *</label>
                    <input
                      type="text"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">住所1 *</label>
                  <input
                    type="text"
                    value={shippingAddress.address1}
                    onChange={(e) => setShippingAddress({...shippingAddress, address1: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">住所2（マンション名など）</label>
                  <input
                    type="text"
                    value={shippingAddress.address2}
                    onChange={(e) => setShippingAddress({...shippingAddress, address2: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                {/* 住所保存チェックボックス */}
                <div className="mt-4 p-3 bg-gray-600 rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveAddress}
                      onChange={(e) => setSaveAddress(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-white">
                      この住所を保存して次回から入力を省略する
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* ステップ3: 確認 */}
          {!showErrorScreen && currentStep === 3 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">注文内容の確認</h3>
              
              {/* テストモード切り替え（管理者のみ表示） */}
              {isAdmin && (
                <div className="mb-4 p-3 bg-yellow-900/50 border border-yellow-600 rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={testMode}
                      onChange={(e) => setTestMode(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-yellow-400">
                      テストモード {testMode ? '（ドラフト注文・実際には発送されません）' : '（通常注文・実際に発送されます）'}
                    </span>
                  </label>
                </div>
              )}

              {/* 価格内訳 */}
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>商品価格 {quantity > 1 && `(${selectedVariant?.price.toLocaleString()}円 × ${quantity}個)`}</span>
                    <span>¥{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>送料 {quantity > 1 && `(${quantity}個分)`}</span>
                    <span>¥{shippingCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>消費税（10%）</span>
                    <span>¥{Math.floor(tax).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-gray-600 pt-2 flex justify-between font-semibold text-white">
                    <span>合計</span>
                    <span className="text-lg">¥{Math.floor(total).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* 配送先情報 */}
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">配送先</h4>
                <div className="text-sm text-gray-400 space-y-1">
                  <p>{shippingAddress.name}</p>
                  <p>〒{shippingAddress.zip}</p>
                  <p>{shippingAddress.state_name} {shippingAddress.city}</p>
                  <p>{shippingAddress.address1}</p>
                  {shippingAddress.address2 && <p>{shippingAddress.address2}</p>}
                  <p>TEL: {shippingAddress.phone}</p>
                  <p>Email: {shippingAddress.email}</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 rounded text-blue-300 text-sm">
                {testMode 
                  ? 'ドラフト注文として作成されます。決済は行われますが、実際の製造・発送は行われません。'
                  : '次のステップでクレジットカード情報を入力して決済を行います。'}
              </div>

              <button
                onClick={async () => {
                  // testModeに関わらず決済ステップへ進む（両方とも決済が必要）
                    setLoading(true);
                    try {
                      const paymentData = {
                        user_id: userId,
                        catalog_id: catalogId,
                        variant_id: selectedVariant?.variant_id,
                        quantity: quantity,
                        shipping_cost: shippingCost,
                        shippingAddress: shippingAddress,
                        recipient: {
                          ...shippingAddress,
                          country_code: 'JP'
                        }
                      };
                      
                      // PaymentIntent作成
                      const response = await fetch(`${AISHA_API_BASE}/printful/purchase/payment-intent/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          user_id: userId,
                          catalog_id: catalogId,
                          variant_id: selectedVariant?.variant_id,
                          quantity: quantity,
                          shipping_cost: shippingCost,
                          recipient: {
                            ...shippingAddress,
                            country_code: 'JP'
                          }
                        })
                      });
                      
                      const data = await response.json();
                      if (data.success) {
                        setPaymentIntentId(data.payment_intent_id);
                        setClientSecret(data.client_secret);
                        setCurrentStep(4); // 決済ステップへ
                      } else {
                        setError(data.error || 'PaymentIntentの作成に失敗しました');
                      }
                    } catch (err) {
                      setError('エラーが発生しました');
                      console.error(err);
                    } finally {
                      setLoading(false);
                    }
                }}
                disabled={loading}
                className={`w-full py-3 rounded font-medium transition-colors ${
                  loading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : testMode
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {loading ? '処理中...' : '決済へ進む'}
              </button>
            </div>
          )}

          {/* ステップ4: 決済 */}
          {!showErrorScreen && currentStep === 4 && clientSecret && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <CreditCardIcon className="w-5 h-5 mr-2" />
                クレジットカード決済
              </h3>

              {/* 保存済みカードがあり、新規カード入力でない場合 */}
              {savedCards.length > 0 && !useNewCard ? (
                <SavedCardsPayment
                  userId={userId}
                  totalAmount={Math.floor(total)}
                  onSelectCard={async (cardId) => {
                    setLoading(true);
                    setError('');
                    try {
                      const response = await fetch(`${AISHA_API_BASE}/stripe/payment-with-saved-card/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          user_id: userId,
                          payment_method_id: cardId,
                          amount: Math.floor(total),
                          credit_amount: 0
                        })
                      });
                      const data = await response.json();
                      if (data.success) {
                        // 決済成功後、Printful注文を確定
                        const confirmResponse = await fetch(`${AISHA_API_BASE}/printful/purchase/confirm-payment/`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            payment_intent_id: data.payment_intent_id,
                            user_id: userId,
                            order_data: {
                              catalog_id: catalogId,
                              variant_id: selectedVariant?.variant_id,
                              quantity: quantity,
                              unit_price: selectedVariant?.price,
                              subtotal: subtotal,
                              shipping_cost: shippingCost,
                              tax: tax,
                              total_amount: Math.floor(total),
                              recipient: { ...shippingAddress, country_code: 'JP' },
                              image_url: imageUrl,
                              suzuri_merchandise_id: merchandiseId
                            },
                            test_mode: testMode
                          })
                        });
                        const confirmData = await confirmResponse.json();
                        if (confirmData.success) {
                          setOrderResult(confirmData);
                          setCurrentStep(5);
                          if (onPurchaseCompleted) onPurchaseCompleted();
                        } else {
                          if (confirmData.error_code) {
                            setErrorCode(confirmData.error_code);
                            setErrorMessage(getErrorInfo(confirmData.error_code).message);
                            setShowErrorScreen(true);
                          } else {
                            setError(confirmData.error || '注文の確定に失敗しました');
                          }
                        }
                      } else {
                        setError(data.error || '決済に失敗しました');
                      }
                    } catch (err) {
                      setError('決済処理中にエラーが発生しました');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onPaymentSuccess={async () => {
                    // 保存済みカード決済成功後、注文を確定
                    setLoading(true);
                    try {
                      const response = await fetch(`${AISHA_API_BASE}/printful/purchase/confirm-payment/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          payment_intent_id: paymentIntentId,
                          user_id: userId,
                          order_data: {
                            catalog_id: catalogId,
                            variant_id: selectedVariant?.variant_id,
                            quantity: quantity,
                            unit_price: selectedVariant?.price,
                            subtotal: subtotal,
                            shipping_cost: shippingCost,
                            tax: tax,
                            total_amount: Math.floor(total),
                            recipient: { ...shippingAddress, country_code: 'JP' },
                            image_url: imageUrl,
                            suzuri_merchandise_id: merchandiseId
                          },
                          test_mode: testMode
                        })
                      });
                      const data = await response.json();
                      if (data.success) {
                        setOrderResult(data);
                        setCurrentStep(5);
                        if (onPurchaseCompleted) onPurchaseCompleted();
                      } else {
                        if (data.error_code) {
                          setErrorCode(data.error_code);
                          setErrorMessage(getErrorInfo(data.error_code).message);
                          setShowErrorScreen(true);
                        } else {
                          setError(data.error || '注文の確定に失敗しました');
                        }
                      }
                    } catch (err) {
                      setError('エラーが発生しました');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onPaymentError={(error) => setError(error)}
                  onUseNewCard={() => setUseNewCard(true)}
                  onCancel={() => setCurrentStep(3)}
                  savedCards={savedCards}
                  onCardsUpdate={fetchSavedCards}
                />
              ) : (
                <>
                  {savedCards.length > 0 && (
                    <button
                      onClick={() => setUseNewCard(false)}
                      className="mb-3 text-indigo-400 hover:text-indigo-300 text-sm flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      保存済みカードに戻る
                    </button>
                  )}
                  <StripePaymentForm
                    userId={userId}
                    chargeAmount={Math.floor(total)}
                    creditAmount={0}
                    existingClientSecret={clientSecret}
                    existingPaymentIntentId={paymentIntentId || undefined}
                    defaultPostalCode={shippingAddress.zip}
                    onSuccess={async () => {
                      setLoading(true);
                      try {
                        const response = await fetch(`${AISHA_API_BASE}/printful/purchase/confirm-payment/`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            payment_intent_id: paymentIntentId,
                            user_id: userId,
                            order_data: {
                              catalog_id: catalogId,
                              variant_id: selectedVariant?.variant_id,
                              quantity: quantity,
                              unit_price: selectedVariant?.price,
                              subtotal: subtotal,
                              shipping_cost: shippingCost,
                              tax: tax,
                              total_amount: Math.floor(total),
                              recipient: { ...shippingAddress, country_code: 'JP' },
                              image_url: imageUrl,
                              suzuri_merchandise_id: merchandiseId
                            },
                            test_mode: testMode
                          })
                        });
                        const data = await response.json();
                        if (data.success) {
                          setOrderResult(data);
                          setCurrentStep(5);
                          if (onPurchaseCompleted) onPurchaseCompleted();
                        } else {
                          if (data.error_code) {
                            setErrorCode(data.error_code);
                            setErrorMessage(getErrorInfo(data.error_code).message);
                            setShowErrorScreen(true);
                          } else {
                            setError(data.error || '注文の確定に失敗しました');
                          }
                        }
                      } catch (err) {
                        setError('エラーが発生しました');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    onError={(error) => setError(error)}
                    onCancel={() => setCurrentStep(3)}
                  />
                </>
              )}
            </div>
          )}

          {/* ステップ5: 完了 */}
          {/* ステップ5: 完了 */}
          {!showErrorScreen && currentStep === 5 && orderResult && (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {orderResult.test_mode ? 'テスト注文が完了しました！' : '注文が送信されました！'}
              </h3>
              
              <div className="mt-4 p-4 bg-gray-700 rounded-lg text-left max-w-md mx-auto">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Printful注文詳細</h4>
                <div className="space-y-2 text-sm text-gray-400">
                  <p>注文ID: {orderResult.order?.id}</p>
                  <p>ステータス: {(() => {
                    const status = orderResult.order?.status;
                    if (status === 'draft') return 'ドラフト（下書き保存済み）';
                    if (status === 'pending') return '処理待ち';
                    if (status === 'failed') return '失敗';
                    if (status === 'canceled') return 'キャンセル済み';
                    if (status === 'onhold') return '保留中';
                    if (status === 'inprocess') return '処理中';
                    if (status === 'fulfilled') return '発送済み';
                    return status || '不明';
                  })()}</p>
                </div>
              </div>

              {orderResult.test_mode && (
                <div className="mt-4 p-4 bg-yellow-900/50 border border-yellow-600 rounded-lg max-w-md mx-auto">
                  <p className="text-yellow-400 text-sm">
                    これはドラフト注文です。Printfulの管理画面で確認できますが、実際の製造・発送は行われません。
                  </p>
                </div>
              )}

              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                閉じる
              </button>
            </div>
          )}
        </div>

        {/* フッター */}
        {!showErrorScreen && currentStep < 4 && currentStep !== 4 && (
          <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-between items-center">
            {currentStep > 1 && (
              <button
                onClick={handlePreviousStep}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
              >
                戻る
              </button>
            )}
            
            <div className="ml-auto">
              {currentStep < 3 && (
                <button
                  onClick={handleNextStep}
                  disabled={
                    (currentStep === 1 && !selectedVariant) ||
                    (currentStep === 2 && !validateShippingForm())
                  }
                  className={`px-6 py-2 rounded font-medium transition-colors ${
                    ((currentStep === 1 && selectedVariant) || (currentStep === 2 && validateShippingForm()))
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  次へ
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintfulPurchaseModal;